/**
 * Timeline Component
 * 
 * Timeline-style page displaying wedding events in a vertical timeline format.
 * Similar to the HTML timeline design with timeline dots and connecting lines.
 */

import React from 'react';
import '../App.css';

// Centralized imports
import { useAuth, AuthGuard } from '../utils/auth';
import { useScrollWobble } from '../utils/scrollAnimations';
import { PAGE_EVENTS, VENUE_INFO } from '../data/eventsData';
import ClosingSection from '../components/shared/ClosingSection';

function Timeline() {
  // Use centralized authentication hook
  const { isValidAccess, isLoading } = useAuth('/tmlne4739hjk');
  
  // Use centralized scroll animation hook
  useScrollWobble();


  // Get all events from centralized data
  const events = PAGE_EVENTS.home();

  // Group events by date
  const eventsByDate = events.reduce((acc, event) => {
    const date = event.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {});

  // Sort dates to ensure proper order
  const sortedDates = Object.keys(eventsByDate).sort((a, b) => {
    // Parse dates for proper sorting
    const dateA = new Date(a.replace(/(\d+)(st|nd|rd|th)/, '$1'));
    const dateB = new Date(b.replace(/(\d+)(st|nd|rd|th)/, '$1'));
    return dateA - dateB;
  });

  // Function to get appropriate header and ornament for each date
  const getDateHeaderInfo = (date) => {
    if (date === 'Saturday, 22nd November 2025') {
      return { title: 'Pre-Wedding Ceremonies', ornament: '🌷' };
    } else if (date === 'Monday, 24th November 2025') {
      return { title: 'Pre-Wedding Celebrations', ornament: '✨' };
    } else if (date === 'Tuesday, 25th Nov 2025') {
      return { title: 'Wedding Day & Reception', ornament: '💕' };
    } else {
      return { title: 'Wedding Events', ornament: '🌸' };
    }
  };

  return (
    <AuthGuard isLoading={isLoading} isValidAccess={isValidAccess}>
      <div className="app">
        {/* Hero Section */}
        <div className="hero">
          <div className="hero-text">
            <img src={`${process.env.PUBLIC_URL}/logo2.png`} alt="Wedding Logo" className="hero-logo" />
            <h1>Radhika & Vivek</h1>
            <p>warmly welcome you to their Wedding</p>
            <p>22nd, 24th & 25th Nov 2025</p>
          </div>
        </div>

        {/* Hero Text Section for Mobile */}
        <div className="hero-text-section">
          <div className="hero-text-content">
            <img src={`${process.env.PUBLIC_URL}/logo2.png`} alt="Wedding Logo" className="hero-logo" />
            <h1>Radhika & Vivek</h1>
            <p>warmly welcome you to their Wedding</p>
            <p>22nd, 24th & 25th Nov 2025</p>
          </div>
        </div>

        <div className="gradient-container">
          {/* Location Section */}
          <div className="location-section">
            <div className="location-container">
              <div className="location-title">
                <img src="/location.png" alt="Location" className="location-icon" />
                <h2>Wedding Venue</h2>
              </div>
              <div className="location-details">
                <h3>{VENUE_INFO.name}</h3>
                <div className="location-address">
                  {VENUE_INFO.address.map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Timeline Events Section */}
          <div className="agenda-timeline-page">
            <div className="timeline-header">
              <h1>Schedule</h1>
            </div>
            
            {sortedDates.map((date, dateIndex) => {
              const headerInfo = getDateHeaderInfo(date);
              return (
                <div key={dateIndex} className="date-timeline-section">
                  {/* Date Header matching FullAgenda style */}
                  <div className="day-section">
                    <div className="day-header">
                      <h2>{headerInfo.title}</h2>
                      <div className="date-ornament">
                        <span className="ornament">{headerInfo.ornament}</span>
                        <span className="date">{date}</span>
                        <span className="ornament">{headerInfo.ornament}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="timeline">
                    {eventsByDate[date].map((event, eventIndex) => (
                      <div key={eventIndex} className={`timeline-event ${eventIndex % 2 === 1 ? 'timeline-event-reverse' : ''}`}>
                        <div className="timeline-content">
                          <div className="timeline-image">
                            <img src={event.image} alt={event.title} className="timeline-icon event-image" />
                          </div>
                          <div className="timeline-text">
                            <h2>{event.title}</h2>
                            <time>{event.time}</time>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Closing Section */}
          <ClosingSection variant="simple" />
        </div>
      </div>
    </AuthGuard>
  );
}

export default Timeline;